"use client";

import React, { useCallback, memo } from "react";
import { CloseButton, Drawer, Portal, Box } from "@chakra-ui/react";
import { useSystemDesign } from "@/shared/system-design";

type OpenChangeDetails = {
  open: boolean;
};

export type WithDrawerOpenProps = {
  title?: string;
  isOpen: boolean;
  onOpenChangeAction: (isOpen: boolean) => void;
  onCloseAction?: () => void;
  children: React.ReactNode;
};

function WithDrawerOpen({
  isOpen,
  onOpenChangeAction,
  title = "Drawer",
  onCloseAction,
  children,
}: WithDrawerOpenProps) {
  const { drawerSize } = useSystemDesign();

  const handleClose = useCallback(() => {
    onCloseAction?.();
    onOpenChangeAction(false);
  }, [onCloseAction, onOpenChangeAction]);

  const handleOpenChange = useCallback(
    (details: OpenChangeDetails) => {
      onOpenChangeAction(details.open);
    },
    [onOpenChangeAction],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      size={drawerSize}
      onEscapeKeyDown={handleClose}
      onInteractOutside={handleClose}
      lazyMount={true}
      unmountOnExit={true}
      immediate={true}
      // Drawer tasks should open to their orientation/context, not to an
      // editable field that opens the mobile keyboard before the user acts.
      restoreFocus={false}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header
              //
              p={{ base: "4", md: "6" }}
            >
              <Box
                maxWidth="full"
                overflow="hidden"
                wordBreak="break-word"
                whiteSpace="normal"
              >
                <Drawer.Title>{title}</Drawer.Title>
              </Box>
            </Drawer.Header>
            <Drawer.Body
              pb="8"
              px={{ base: "2", md: "6" }}
              pt={{ base: "0", md: "6" }}
            >
              {children}
            </Drawer.Body>

            <Drawer.CloseTrigger asChild onClick={handleClose}>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}

export default memo(WithDrawerOpen);
